/**
 * InsertWithdrawalRequestPage — "ادراج طلب سحب" (iter-286, Feb 2026)
 * ================================================================
 * Manual entry point for creating a withdrawal request against a
 * Merchant Platform account.
 *
 * iter-286 (Feb 2026) — Overhauled per the "sync spec":
 *   • Search targets Merchant Platform users (not local Wakeel/agents).
 *   • After picking, the current wallet balance is displayed and used as
 *     the max cap for the amount input.
 *   • When method = bank_transfer, the merchant's saved bank(s) are
 *     rendered as a picker. If empty, an "Add Bank" inline form appears
 *     so the operator can capture the details on-the-fly.
 *   • The withdrawal date is prefilled to today with a datetime-local
 *     editor so the operator can back-date when needed.
 *
 * The "unregistered agent" fallback flow remains unchanged.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowDownToLine, Search, Loader2, User, Mail, Phone,
  Wallet, Save, CheckCircle2, XCircle, Plus, Calendar,
  Building2, Banknote,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Formats a Date instance to the `datetime-local` shape (YYYY-MM-DDTHH:mm).
function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function InsertWithdrawalRequestPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('registered'); // 'registered' | 'unregistered'

  // Registered search
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundAgent, setFoundAgent] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Unregistered form
  const [name, setName] = useState('');
  const [emailU, setEmailU] = useState('');
  const [phoneU, setPhoneU] = useState('');

  // Withdrawal form
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [selectedBankIdx, setSelectedBankIdx] = useState(-1); // -1 = manual entry
  const [bankName, setBankName] = useState('');
  const [iban, setIban] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [addingBank, setAddingBank] = useState(false);

  const [customDate, setCustomDate] = useState(() => toDatetimeLocal(new Date()));

  const [saving, setSaving] = useState(false);

  // Reset bank selection whenever the found agent changes.
  useEffect(() => {
    if (!foundAgent) {
      setSelectedBankIdx(-1);
      setBankName('');
      setIban('');
      setAccountName('');
      setAccountNumber('');
      setAddingBank(false);
      return;
    }
    const banks = foundAgent.bank_accounts || [];
    if (banks.length > 0) {
      // pick the primary (or first) bank by default
      const primaryIdx = banks.findIndex((b) => b.is_primary);
      const idx = primaryIdx >= 0 ? primaryIdx : 0;
      setSelectedBankIdx(idx);
      const b = banks[idx];
      setBankName(b.bank_name || '');
      setIban(b.iban || '');
      setAccountName(b.account_name || foundAgent.name || '');
      setAccountNumber(b.account_number || '');
      setAddingBank(false);
    } else {
      // No saved banks — open the Add Bank form.
      setSelectedBankIdx(-1);
      setAddingBank(true);
      setAccountName(foundAgent.name || '');
      setBankName('');
      setIban('');
      setAccountNumber('');
    }
  }, [foundAgent]);

  const pickBank = (idx) => {
    setSelectedBankIdx(idx);
    setAddingBank(false);
    const b = (foundAgent?.bank_accounts || [])[idx] || {};
    setBankName(b.bank_name || '');
    setIban(b.iban || '');
    setAccountName(b.account_name || foundAgent?.name || '');
    setAccountNumber(b.account_number || '');
  };

  const startAddBank = () => {
    setSelectedBankIdx(-1);
    setAddingBank(true);
    setBankName('');
    setIban('');
    setAccountName(foundAgent?.name || '');
    setAccountNumber('');
  };

  const searchAgent = async (overrideEmail = null) => {
    const email = (overrideEmail || searchEmail).trim().toLowerCase();
    if (!email) {
      toast.error(t('أدخل البريد الإلكتروني', 'Enter an email'));
      return;
    }
    setSearching(true);
    setNotFound(false);
    setFoundAgent(null);
    try {
      const r = await axios.get(`${API}/admin/merchant-users/by-email/${encodeURIComponent(email)}`,
                                { headers: getAuthHeaders() });
      setFoundAgent(r.data || null);
      toast.success(t('تم إيجاد الحساب', 'Account found'));
    } catch (err) {
      const st = err?.response?.status;
      if (st === 404) {
        setNotFound(true);
        toast.error(t('لم يتم إيجاد حساب في منصة التجار بهذا البريد', 'No merchant account with that email'));
      } else {
        toast.error(err.response?.data?.detail || err.message);
      }
    } finally {
      setSearching(false);
    }
  };

  // iter-299d — Auto-search when the page is opened via
  // "Withdraw from wallet" button on the agent details page
  // (`/dashboard/finance/cloudgm/merchants/insert-withdrawal-request?email=...`).
  const autoSearched = useRef(false);
  useEffect(() => {
    const presetEmail = searchParams.get('email');
    if (presetEmail && !autoSearched.current) {
      autoSearched.current = true;
      setMode('registered');
      setSearchEmail(presetEmail);
      searchAgent(presetEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const walletBalance = Number(foundAgent?.wallet_balance || foundAgent?.commission_balance || 0);
  const amountNum = Number(amount || 0);
  const exceedsBalance = mode === 'registered' && walletBalance > 0 && amountNum > walletBalance;

  const canSubmit = useMemo(() => {
    if (amountNum <= 0) return false;
    if (mode === 'registered' && !foundAgent) return false;
    if (mode === 'unregistered' && (!name.trim() || !emailU.trim() || !phoneU.trim())) return false;
    if (method === 'bank_transfer') {
      if (!bankName.trim() || !iban.trim()) return false;
    }
    return true;
  }, [mode, foundAgent, amountNum, name, emailU, phoneU, method, bankName, iban]);

  const submit = async () => {
    if (!canSubmit) return;
    if (exceedsBalance) {
      const ok = window.confirm(t(
        `المبلغ المطلوب (${amountNum} AED) أكبر من الرصيد المتاح (${walletBalance} AED). المتابعة؟`,
        `Requested (${amountNum} AED) is larger than the available balance (${walletBalance} AED). Continue?`,
      ));
      if (!ok) return;
    }
    setSaving(true);
    try {
      const body = {
        amount: amountNum,
        currency: 'AED',
        method,
        account_details: {
          bank_name:      bankName.trim(),
          iban:           iban.trim(),
          account_name:   accountName.trim() || (mode === 'registered' ? (foundAgent?.name || '') : name.trim()),
          account_number: accountNumber.trim(),
        },
        custom_created_at: customDate ? new Date(customDate).toISOString() : null,
      };
      if (mode === 'registered') {
        body.user_id = foundAgent.id;
      } else {
        body.unregistered = true;
        body.name  = name.trim();
        body.email = emailU.trim().toLowerCase();
        body.phone = phoneU.trim();
      }
      const r = await axios.post(`${API}/admin/withdrawal-requests`, body, { headers: getAuthHeaders() });
      toast.success(t('تم إنشاء طلب السحب', 'Withdrawal request created'));
      navigate(`/dashboard/finance/cloudgm/merchants/withdrawal-requests/${r.data?.data?.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const savedBanks = foundAgent?.bank_accounts || [];

  return (
    <DashboardLayout
      title="ادراج طلب سحب"
      titleEn="Insert Withdrawal Request"
      subtitle="إنشاء طلب سحب يدوي لحساب مسجل في منصة التجار أو حساب غير مسجل"
      subtitleEn="Manually create a withdrawal request for a Merchant Platform account (registered or not)"
      screenCode="FIN-MERCH-WD-INS"
      backPath="/dashboard/finance/cloudgm/merchants"
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)', labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'ادراج طلب سحب', labelEn: 'Insert Withdrawal Request', path: null },
      ]}
    >
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mode selector */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          {['registered', 'unregistered'].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setFoundAgent(null); setNotFound(false); }}
              className={`p-4 rounded-xl border-2 text-start transition-all ${
                mode === m
                  ? 'bg-orange-500/15 border-orange-500 text-white'
                  : 'bg-white/[0.02] border-white/10 text-white/70 hover:bg-white/5'
              }`}
              data-testid={`wd-mode-${m}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4" />
                <span className="font-semibold">
                  {m === 'registered' ? t('حساب مسجل في منصة التجار', 'Registered Merchant Account') : t('حساب غير مسجل', 'Unregistered Account')}
                </span>
              </div>
              <p className="text-[11px] text-white/60">
                {m === 'registered'
                  ? t('ابحث بالبريد الإلكتروني في منصة التجار', 'Search by email on the Merchant Platform')
                  : t('اسم + بريد + هاتف — يُحفظ للمستقبل', 'Name + email + phone — saved for future sync')}
              </p>
            </button>
          ))}
        </motion.div>

        {/* Registered flow — email search */}
        {mode === 'registered' && (
          <motion.section
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <Search className="h-4 w-4 text-orange-400" />
              <h3 className="font-semibold text-white">
                {t('بحث بالبريد الإلكتروني', 'Search by email')}
              </h3>
            </div>
            <div className="flex gap-2">
              <Input
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="merchant@example.com"
                dir="ltr"
                className="flex-1"
                data-testid="wd-search-email"
                onKeyDown={(e) => { if (e.key === 'Enter') searchAgent(); }}
              />
              <Button onClick={searchAgent} disabled={searching || !searchEmail.trim()}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="wd-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ms-2">{t('بحث', 'Search')}</span>
              </Button>
            </div>

            {foundAgent && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
                   data-testid="wd-found-agent">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  <span className="text-emerald-200 font-semibold text-sm">
                    {t('تم إيجاد الحساب في منصة التجار', 'Merchant account found')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <div><span className="text-white/50">{t('الاسم:', 'Name:')}</span> <span className="text-white ms-1">{foundAgent.name}</span></div>
                  <div><span className="text-white/50">{t('البريد:', 'Email:')}</span> <span className="text-white ms-1" dir="ltr">{foundAgent.email}</span></div>
                  <div><span className="text-white/50">{t('الهاتف:', 'Phone:')}</span> <span className="text-white ms-1" dir="ltr">{foundAgent.phone || '—'}</span></div>
                  <div><span className="text-white/50">{t('الدور:', 'Role:')}</span> <span className="text-white ms-1">{foundAgent.role}</span></div>
                </div>
                {/* iter-286 — Wallet balance summary */}
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-between"
                     data-testid="wd-wallet-balance">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-200 text-[12px] font-semibold">
                      {t('الرصيد المتاح للسحب', 'Available balance')}
                    </span>
                  </div>
                  <span className="text-amber-100 font-bold text-base" dir="ltr">
                    {walletBalance.toLocaleString()} AED
                  </span>
                </div>
              </div>
            )}

            {notFound && (
              <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2"
                   data-testid="wd-not-found">
                <XCircle className="h-4 w-4 text-red-400" />
                <p className="text-[12px] text-red-200">
                  {t('لم يتم إيجاد حساب في منصة التجار بهذا البريد. جرّب الوضع "غير مسجل" بدلاً منه.',
                     'No merchant account found. Try the "Unregistered" mode instead.')}
                </p>
              </div>
            )}
          </motion.section>
        )}

        {/* Unregistered flow — direct entry */}
        {mode === 'unregistered' && (
          <motion.section
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-orange-400" />
              <h3 className="font-semibold text-white">{t('بيانات الوكيل', 'Agent Details')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px]">{t('الاسم', 'Name')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t('اسم الوكيل الكامل', 'Full name')}
                       data-testid="wd-u-name" />
              </div>
              <div>
                <Label className="text-[11px]">{t('البريد', 'Email')}</Label>
                <Input value={emailU} onChange={(e) => setEmailU(e.target.value)}
                       placeholder="agent@example.com" dir="ltr"
                       data-testid="wd-u-email" />
              </div>
              <div>
                <Label className="text-[11px]">{t('رقم الهاتف', 'Phone')}</Label>
                <Input value={phoneU} onChange={(e) => setPhoneU(e.target.value)}
                       placeholder="+9715..." dir="ltr"
                       data-testid="wd-u-phone" />
              </div>
            </div>
            <p className="text-[10px] text-white/40 mt-2 flex items-center gap-1">
              <Mail className="h-2.5 w-2.5" />
              {t('البريد سيُحفظ كسجل. عند تسجيل هذا الوكيل مستقبلاً على منصة التجار، سيرى الطلب في حسابه تلقائياً.',
                 'The email is saved. When this agent later registers on the merchant platform, they will see this request automatically.')}
            </p>
          </motion.section>
        )}

        {/* Withdrawal details */}
        <motion.section
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-4 w-4 text-orange-400" />
            <h3 className="font-semibold text-white">{t('تفاصيل السحب', 'Withdrawal Details')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">{t('المبلغ (AED)', 'Amount (AED)')}</Label>
              <Input type="number" min="0" step="0.01"
                     value={amount} onChange={(e) => setAmount(e.target.value)}
                     placeholder="0.00" dir="ltr"
                     data-testid="wd-amount" />
              {exceedsBalance && (
                <p className="text-[11px] text-amber-300 mt-1 flex items-center gap-1"
                   data-testid="wd-amount-warning">
                  <XCircle className="h-3 w-3" />
                  {t(`المبلغ يتجاوز الرصيد المتاح (${walletBalance.toLocaleString()} AED)`,
                     `Exceeds available balance (${walletBalance.toLocaleString()} AED)`)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-[11px]">{t('طريقة الدفع', 'Method')}</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                      className="w-full h-10 bg-[#0a0a0a] border border-white/10 rounded-md px-2 text-white text-sm"
                      data-testid="wd-method">
                <option value="bank_transfer">{t('تحويل بنكي', 'Bank Transfer')}</option>
                <option value="cash">{t('نقدي', 'Cash')}</option>
                <option value="wallet">{t('محفظة', 'Wallet')}</option>
              </select>
            </div>

            {/* iter-286 — Custom withdrawal date */}
            <div className="md:col-span-2">
              <Label className="text-[11px] flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {t('تاريخ السحب', 'Withdrawal Date')}
              </Label>
              <Input type="datetime-local"
                     value={customDate}
                     onChange={(e) => setCustomDate(e.target.value)}
                     className="[color-scheme:dark]"
                     data-testid="wd-custom-date" />
              <p className="text-[10px] text-white/40 mt-1">
                {t('يعبأ تلقائياً بتاريخ اليوم. يمكنك تعديله عند إدراج طلب سابق.',
                   'Prefilled with today. Edit when back-entering a request.')}
              </p>
            </div>
          </div>

          {/* Bank details section — only for bank_transfer */}
          {method === 'bank_transfer' && (
            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-orange-400" />
                  <h4 className="font-semibold text-white text-sm">{t('بيانات البنك', 'Bank Details')}</h4>
                </div>
                {mode === 'registered' && (savedBanks.length > 0 || addingBank) && (
                  <Button variant="ghost" size="sm"
                          onClick={addingBank ? () => pickBank(savedBanks.length > 0 ? 0 : -1) : startAddBank}
                          className="text-[11px] text-orange-300 hover:text-orange-200"
                          data-testid="wd-toggle-add-bank">
                    {addingBank
                      ? t('استخدام بنك محفوظ', 'Use a saved bank')
                      : (<><Plus className="h-3 w-3 me-1" /> {t('إضافة بنك', 'Add Bank')}</>)}
                  </Button>
                )}
              </div>

              {/* Saved banks picker */}
              {mode === 'registered' && !addingBank && savedBanks.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="wd-saved-banks">
                  {savedBanks.map((b, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => pickBank(idx)}
                      className={`p-3 rounded-lg border text-start transition-all ${
                        selectedBankIdx === idx
                          ? 'bg-emerald-500/10 border-emerald-500 text-white'
                          : 'bg-white/[0.02] border-white/10 text-white/70 hover:bg-white/5'
                      }`}
                      data-testid={`wd-bank-option-${idx}`}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Banknote className="h-3 w-3" />
                        {b.bank_name || t('بنك بدون اسم', 'Unnamed bank')}
                        {b.is_primary && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                            {t('رئيسي', 'Primary')}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-white/50 mt-1 font-mono" dir="ltr">
                        IBAN: {b.iban ? b.iban.slice(0, 6) + '…' + b.iban.slice(-4) : '—'}
                      </div>
                      {b.account_name && (
                        <div className="text-[11px] text-white/50" dir="ltr">{b.account_name}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Bank form — either "add new" for registered, or default input mode */}
              {(mode === 'unregistered' || addingBank || savedBanks.length === 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wd-bank-form">
                  <div>
                    <Label className="text-[11px]">{t('اسم البنك', 'Bank Name')} *</Label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)}
                           data-testid="wd-bank" />
                  </div>
                  <div>
                    <Label className="text-[11px]">IBAN *</Label>
                    <Input value={iban} onChange={(e) => setIban(e.target.value)} dir="ltr"
                           data-testid="wd-iban" />
                  </div>
                  <div>
                    <Label className="text-[11px]">{t('اسم صاحب الحساب', 'Account Name')}</Label>
                    <Input value={accountName} onChange={(e) => setAccountName(e.target.value)}
                           data-testid="wd-account-name" />
                  </div>
                  <div>
                    <Label className="text-[11px]">{t('رقم الحساب', 'Account Number')}</Label>
                    <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} dir="ltr"
                           data-testid="wd-account-number" />
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.section>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={!canSubmit || saving}
                  className="bg-orange-600 hover:bg-orange-700 h-12 px-8 text-base font-semibold"
                  data-testid="wd-submit">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            <span className="ms-2">{t('إدراج الطلب', 'Insert Request')}</span>
          </Button>
        </div>
      </section>
    </DashboardLayout>
  );
}
